import React, { RefCallback, useCallback, useMemo, useState } from 'react';
import {
  components as ReactSelectComponents,
  InputActionMeta,
  SelectInstance,
  StylesConfig,
} from 'react-select';
import { CreatableReactSelectMenu } from '../../Dropdown/StyledReactSelect';
import EntitySelectionPanel from './EntitySelectionPanel';
import {
  IEntitySelectionContext,
  IEntitySelectionProps,
  ISelectedEntity,
} from './types';
import { EntitySelectionContext } from './context';
import { isNil } from 'lodash';
import EntitySelectionMultiValue from './label/EntitySelectionMultiValue';
import { colors } from '../../../config/styles';
import { EmptyComponent } from '../../Dropdown/StyledReactSelect/SelectUtils';
import { getReactSelectOptionId } from './reactSelectUtils';
import { WidgetButtonWrapper } from './EntitySelectionPanelUI';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { useFloatingLayerContext } from '../../FloatingManager';

const EntitySelection = ({
  tags,
  schemaId,
  isError,
  selectedEntities,
  onSelectEntity,
  onRemoveEntity,
  onCreateEntity,
  labelProps,
  onClose,
  onOpen,
  placeholder,
  autoFocus,
  isDisabled,
  className,
  isReadOnly,
  disabledClickHandler,
  disabledEntities,
  onCreateCustom,
}: IEntitySelectionProps) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setLoading] = useState(false);

  const openHandler = useCallback(() => {
    // needs an extra check for menuIsOpen because React Select fires onOpen handler even if the menu is open :(
    // and breaks the onOpen contract to be fire only on first open
    if (!menuIsOpen) {
      setMenuIsOpen(true);
      onOpen?.();
    }
  }, [menuIsOpen, onOpen]);

  const closeHandler = useCallback(() => {
    // perform similar guard as openHandler despite that I didn't see calling close handler multiple times
    // this is just ensure that onOpen and onClose are fired only when the actual open/close happens
    if (menuIsOpen) {
      setMenuIsOpen(false);
      setSearchTerm('');
      onClose?.();
    }
  }, [menuIsOpen, onClose]);

  const clickOnDisabledMenuHandler = useCallback(() => {
    if (isDisabled && disabledClickHandler) {
      disabledClickHandler();
    }
  }, [disabledClickHandler, isDisabled]);

  const selectEntityHandler = useCallback(
    (entity: ISelectedEntity) => {
      onSelectEntity?.(entity);
      closeHandler();
    },
    [closeHandler, onSelectEntity],
  );

  const createEntityHandler = useMemo(
    () =>
      !isNil(onCreateEntity) && !isReadOnly
        ? (inputValue: string) => {
            const createdEntity = onCreateEntity?.(inputValue);
            if (!isNil(createdEntity)) {
              setSearchTerm('');
              selectEntityHandler(createdEntity);
            }
          }
        : undefined,
    [isReadOnly, onCreateEntity, selectEntityHandler],
  );

  const createCustomHandler = useMemo(() => {
    return !isNil(onCreateCustom)
      ? () => {
          onCreateCustom();
          closeHandler();
        }
      : undefined;
  }, [closeHandler, onCreateCustom]);

  const selectStyles: StylesConfig<ISelectedEntity, true, never> = {
    menu: (base) => {
      return {
        ...base,
        width: 'auto',
      };
    },
    control: (base) => {
      if (isError) {
        return {
          ...base,
          borderColor: colors.mainError,
        };
      } else {
        return base;
      }
    },
  };

  const readOnlyAwareMenuIsOpen = !isReadOnly ? menuIsOpen : false;
  const readOnlySearchTerm = !isReadOnly ? searchTerm : '';

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    middleware: [flip()],
    whileElementsMounted: autoUpdate,
    open: readOnlyAwareMenuIsOpen,
    onOpenChange: (open) => {
      setMenuIsOpen(open);
      if (!open && searchTerm) {
        setSearchTerm('');
      }
    },
  });

  const dismiss = useDismiss(context, {
    outsidePress: true,
    outsidePressEvent: 'mousedown',
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const reactSelectRefCallback: RefCallback<
    SelectInstance<ISelectedEntity, true, never>
  > = useCallback(
    (ref) => {
      refs.setReference(ref?.controlRef ?? null);
    },
    [refs],
  );

  const entitySelectionContext = useMemo(
    (): IEntitySelectionContext => ({
      onRemoveEntity: !isReadOnly ? onRemoveEntity : undefined,
      labelProps: !isReadOnly ? labelProps : undefined,
      onClose: closeHandler,
    }),
    [isReadOnly, onRemoveEntity, labelProps, closeHandler],
  );

  const zIndex = useFloatingLayerContext();

  return (
    <EntitySelectionContext.Provider value={entitySelectionContext}>
      {/* Wrapper <button> for 2 reasons:
       *  1. Make the widget a single DOM element - useful flexbox contexts with gaps
       *  2. Why <button> - when used in Drag and Drop context as Draggable,
       *     <button> elements do not trigger dragging and do not swallow
       *     mouseDown events preventing React Select from working
       *     https://github.com/atlassian/react-beautiful-dnd/blob/master/
       *     docs/api/draggable.md#interactive-child-elements-within-a-draggable-
       */}
      <WidgetButtonWrapper
        type="button"
        onClick={clickOnDisabledMenuHandler}
        {...getReferenceProps()}
      >
        <CreatableReactSelectMenu<ISelectedEntity, true, never>
          isMulti={true}
          menuPortalTarget={document.body}
          menuIsOpen={readOnlyAwareMenuIsOpen}
          onMenuOpen={!isReadOnly ? openHandler : undefined}
          openMenuOnFocus
          ref={reactSelectRefCallback}
          options={[]} // we build our menu list with EntitySelectionPanel
          inputValue={readOnlySearchTerm}
          value={selectedEntities}
          onInputChange={(newValue: string, actionMeta: InputActionMeta) => {
            if (actionMeta.action === 'input-change' && !isReadOnly) {
              setSearchTerm(newValue);
            }
          }}
          onChange={(_, actionMeta) => {
            // handle backspace to clear a value
            if (
              actionMeta.action === 'pop-value' &&
              // the React Select type is wrong here, it can be null when there are no items
              !isNil(actionMeta.removedValue) &&
              !isReadOnly
            ) {
              onRemoveEntity?.(actionMeta.removedValue);
            }
          }}
          onCreateOption={createEntityHandler}
          styles={selectStyles}
          // required to build keys for selected options
          getOptionValue={getReactSelectOptionId}
          getOptionLabel={getReactSelectOptionId}
          components={{
            MenuList: EmptyComponent, // we are rendering our custom menu
            MultiValue: EntitySelectionMultiValue,
            LoadingIndicator: ReactSelectComponents.LoadingIndicator,
          }}
          placeholder={placeholder}
          className={className}
          isDisabled={isDisabled}
          autoFocus={autoFocus}
          isLoading={isLoading}
        />

        {readOnlyAwareMenuIsOpen && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={{ ...floatingStyles, zIndex }}
              {...getFloatingProps()}
            >
              <EntitySelectionPanel
                setLoading={setLoading}
                schemaId={schemaId}
                tags={tags}
                selectedEntities={selectedEntities}
                onSelectEntity={selectEntityHandler}
                onCreateEntity={createEntityHandler}
                searchTerm={readOnlySearchTerm}
                setSearchTerm={setSearchTerm}
                onClose={closeHandler}
                disabledEntities={disabledEntities}
                onCreateCustom={createCustomHandler}
              />
            </div>
          </FloatingPortal>
        )}
      </WidgetButtonWrapper>
    </EntitySelectionContext.Provider>
  );
};
export default EntitySelection;
